// Hand-maintained from https://api.dev.omnetic.dev/dms/v1/doc/sales_context.json, OfferAlternative endpoints only.
// Every endpoint omits the Authorization parameter: the base query injects the user token.
import {omneticApi as api} from '../../../../../base/src/baseApi/omneticApi';

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    addOfferAlternative: build.mutation<AddOfferAlternativeApiResponse, AddOfferAlternativeApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/offer-alternative`,
        method: 'POST',
        body: queryArg.addOfferAlternativeRequestBody,
        headers: {
          'X-Authorization-Profile': queryArg['X-Authorization-Profile'],
        },
      }),
    }),
    getOfferAlternatives: build.query<GetOfferAlternativesApiResponse, GetOfferAlternativesApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/offer-alternative`,
        headers: {
          'X-Authorization-Profile': queryArg['X-Authorization-Profile'],
        },
      }),
    }),
    applyOfferAlternative: build.mutation<
      ApplyOfferAlternativeApiResponse,
      ApplyOfferAlternativeApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/offer-alternative/${queryArg.alternativeId}/apply`,
        method: 'POST',
        body: queryArg.applyOfferAlternativeRequestBody,
        headers: {
          'X-Authorization-Profile': queryArg['X-Authorization-Profile'],
        },
      }),
    }),
    getVehicleOfferAlternatives: build.query<
      GetVehicleOfferAlternativesApiResponse,
      GetVehicleOfferAlternativesApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/vehicle/${queryArg.vehicleId}/offer-alternative`,
        headers: {
          'X-Authorization-Profile': queryArg['X-Authorization-Profile'],
        },
      }),
    }),
    updateOfferAlternative: build.mutation<
      UpdateOfferAlternativeApiResponse,
      UpdateOfferAlternativeApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/offer-alternative/${queryArg.alternativeId}`,
        method: 'PATCH',
        body: queryArg.updateOfferAlternativeRequestBody,
        headers: {
          'X-Authorization-Profile': queryArg['X-Authorization-Profile'],
        },
      }),
    }),
    addOfferAlternativeUpsellFromCatalog: build.mutation<
      AddOfferAlternativeUpsellFromCatalogApiResponse,
      AddOfferAlternativeUpsellFromCatalogApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/offer-alternative/${queryArg.alternativeId}/upsell-from-catalog`,
        method: 'POST',
        body: queryArg.addOfferAlternativeUpsellFromCatalogRequestBody,
        headers: {
          'X-Authorization-Profile': queryArg['X-Authorization-Profile'],
        },
      }),
    }),
    withdrawOfferAlternative: build.mutation<
      WithdrawOfferAlternativeApiResponse,
      WithdrawOfferAlternativeApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/business-case/${queryArg.businessCaseId}/offer-alternative/${queryArg.alternativeId}`,
        method: 'DELETE',
        headers: {
          'X-Authorization-Profile': queryArg['X-Authorization-Profile'],
        },
      }),
    }),
  }),
  overrideExisting: false,
});
export {injectedRtkApi as offerAlternativeGeneratedApi};
export type AddOfferAlternativeApiResponse = /** status 201  */ UuidResponseBody;
export type AddOfferAlternativeApiArg = {
  businessCaseId: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
  addOfferAlternativeRequestBody: AddOfferAlternativeRequestBody;
};
export type GetOfferAlternativesApiResponse = /** status 200  */ SellerOfferAlternatives;
export type GetOfferAlternativesApiArg = {
  businessCaseId: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
};
export type ApplyOfferAlternativeApiResponse = unknown;
export type ApplyOfferAlternativeApiArg = {
  businessCaseId: string;
  alternativeId: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
  applyOfferAlternativeRequestBody: ApplyOfferAlternativeRequestBody;
};
export type GetVehicleOfferAlternativesApiResponse = /** status 200  */ VehicleOfferAlternatives;
export type GetVehicleOfferAlternativesApiArg = {
  vehicleId: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
};
export type UpdateOfferAlternativeApiResponse = unknown;
export type UpdateOfferAlternativeApiArg = {
  businessCaseId: string;
  alternativeId: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
  updateOfferAlternativeRequestBody: UpdateOfferAlternativeRequestBody;
};
export type AddOfferAlternativeUpsellFromCatalogApiResponse = /** status 201  */ UuidResponseBody;
export type AddOfferAlternativeUpsellFromCatalogApiArg = {
  businessCaseId: string;
  alternativeId: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
  addOfferAlternativeUpsellFromCatalogRequestBody: AddOfferAlternativeUpsellFromCatalogRequestBody;
};
export type WithdrawOfferAlternativeApiResponse = unknown;
export type WithdrawOfferAlternativeApiArg = {
  businessCaseId: string;
  alternativeId: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
};
export type UuidResponseBody = {
  id: string;
};
export type Error = {
  errors?: {
    message?: string;
    code?: string;
  }[];
  validationErrors?: {
    message?: string;
    field?: string;
  }[];
};
export type AddOfferAlternativeRequestBody =
  | {
      configurationId: string;
      vehicleId?: string;
    }
  | {
      configurationId?: string;
      vehicleId: string;
    };
export type BigDecimal = string;
export type Currency = string;
export type Money = {
  amount: BigDecimal;
  currency: Currency;
};
export type VatMoneyResponseBody = {
  withoutVat: Money;
  withVat: Money;
};
export type MoneyResponseBody = {
  amount: string;
  currency: string;
};
export type OfferAlternativeTotalResponseBody = {
  withVat: Money;
  withoutVat: Money;
  vat: Money;
};
export type SellerOfferAlternatives = {
  canAdd: boolean;
  customerChoice: {
    alternativeId: string;
    revision: number;
    chosenAt: string;
    chosenBy: 'CUSTOMER' | 'SELLER';
    isCurrent: boolean;
  } | null;
  alternatives: {
    id: string;
    letter: string;
    name: string | null;
    kind: 'CONFIGURATION' | 'STOCK';
    condition: 'NEW' | 'USED';
    title: string;
    vin: string | null;
    isApplied: boolean;
    revision: number;
    selectable: boolean;
    unavailableReason:
      | (
          | 'EXPIRED'
          | 'PRICE_EXPIRED'
          | 'PRICE_MISSING'
          | 'SOURCE_CHANGED'
          | 'SOURCE_UNAVAILABLE'
          | 'OFFERED_ELSEWHERE'
          | null
        )
      | null;
    availability: 'FACTORY_ORDER' | 'IN_STOCK';
    configurationId: string | null;
    vehicleId: string | null;
    revisedAt: string;
    canEdit: boolean;
    canWithdraw: boolean;
    canApply: boolean;
    terms: {
      currency: string;
      /** VAT as a decimal percentage string, e.g. 21 or 21.00 for 21 percent. Never a fractional rate such as 0.21. */
      vatRate: string;
      /** Null for the applied alternative when its live deal selling price is missing, or when the caller may not read the selling price of this business case. Non-applied alternatives retain their negotiated revision prices. */
      vehiclePrice: VatMoneyResponseBody | null;
      discount: {
        withVat: Money;
        withoutVat: Money;
        percentage: string | null;
        reason: string | null;
      } | null;
      upsell: {
        id: string;
        name: string;
        vatCode: ('S' | 'R' | 'MR' | 'E' | 'Z' | 'SR' | 'P' | null) | null;
        /** Item VAT percentage string, independent of vehicle VAT. */
        vatRate: string;
        /** Null when the user may not see the upsell selling price. */
        sellingPrice: VatMoneyResponseBody | null;
        purchasePriceWithoutVat: MoneyResponseBody | null;
      }[];
      /** Null for the applied alternative when its live deal selling price is missing, or when the caller may not read the total price of this business case. */
      total: OfferAlternativeTotalResponseBody | null;
      validUntil: string | null;
      deliveryNote: string | null;
      customerNote: string | null;
    };
    /** The four price fields are null when an applied alternative has no live deal selling price. They are also null when the caller may not read the price: vehicleSellingPrice, upsell and discount follow the selling price right, totalPrice follows the total price right. A null discount can also mean that there is no discount. */
    summary: {
      vehicleSellingPrice: VatMoneyResponseBody | null;
      upsell: VatMoneyResponseBody | null;
      discount: VatMoneyResponseBody | null;
      totalPrice: OfferAlternativeTotalResponseBody | null;
    };
  }[];
};
export type ApplyOfferAlternativeRequestBody = {
  expectedRevision: number;
};
export type AddOfferAlternativeUpsellFromCatalogRequestBody = {
  productId: string;
  expectedRevision: number;
};
export type VehicleOfferAlternatives = {
  /** Open business cases where this vehicle is a non-withdrawn stock alternative. */
  items: {
    businessCaseId: string;
    /** Business case display code (publicId), not customId. */
    businessCasePublicId: string | null;
    letter: string;
    isApplied: boolean;
  }[];
};
export type UpdateOfferAlternativeRequestBody = {
  expectedRevision: number;
  name?: string | null;
  discount?:
    | (
        | {
            withVat: string;
            percentage?: string;
            reason?: string | null;
          }
        | {
            withVat?: string;
            percentage: string;
            reason?: string | null;
          }
      )
    | null;
  /** Omitted keeps every stored line; a stored line missing from the list is removed. */
  upsell?: {
    /** Id of a stored line of this alternative; any other id or none adds a new line. */
    id?: string | null;
    /** Omitted keeps the stored name; required for a new line. */
    name?: string;
    /** Omitted keeps the stored price; required for a new line. Null without the selling-price view right keeps it too. */
    sellingPriceWithVat?: string | null;
    /** Item VAT code; omitted defaults to standard VAT, as existing upsell creation preselection. */
    vatCode?: 'S' | 'R' | 'MR' | 'E' | 'Z' | 'SR' | 'P';
    /** Item VAT percentage string (21 or 21.00, never a coefficient). Omitted uses the current country rate for vatCode. A supplied rate must match that rate. */
    vatRate?: string;
    purchasePriceWithoutVat?: string | null;
    /** Clears a stored field, also one the user may not see. Not with a value of the same field, not on a new line. */
    clear?: 'purchasePriceWithoutVat'[];
  }[];
  validUntil?: string | null;
  deliveryNote?: string | null;
  customerNote?: string | null;
  source?:
    | {
        configurationId: string;
        vehicleId?: string;
      }
    | {
        configurationId?: string;
        vehicleId: string;
      };
};
export const {
  useAddOfferAlternativeMutation,
  useGetOfferAlternativesQuery,
  useApplyOfferAlternativeMutation,
  useGetVehicleOfferAlternativesQuery,
  useUpdateOfferAlternativeMutation,
  useAddOfferAlternativeUpsellFromCatalogMutation,
  useWithdrawOfferAlternativeMutation,
} = injectedRtkApi;
